/**
 * YOL HARİTASI — 12 aylık stratejik plan.
 *
 * Katmanlar: Aksiyon Planı "şu an", Strateji Seansı "bu ay", burası "bu yıl".
 * Üretim tek Opus çağrısı olduğu için sayfa açılınca KENDİLİĞİNDEN koşmaz;
 * kullanıcı butona basar ve maliyeti önceden görür.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Map, Info, RefreshCw, TriangleAlert } from "lucide-react";

type Is = {
  baslik: string; neden: string;
  kategori: "teknik" | "mimari" | "icerik" | "otorite" | "geo";
  efor: "dusuk" | "orta" | "yuksek";
};
type Ceyrek = {
  no: number; ay: string; tema: string; nedenSimdi: string;
  isler: Is[]; olcut: string; riskler?: string;
};
type Plan = {
  uretildi: string; hedef: string | null; durumOzeti: string; anaHat: string;
  ceyrekler: Ceyrek[]; sinyaller: { etiket: string; deger: string }[];
  uyari: string; maliyet: number;
};
type Yanit = { plan: Plan | null; uretildiAt: string | null; sinyaller: { etiket: string; deger: string }[] };

const KATEGORI_AD: Record<Is["kategori"], string> = {
  teknik: "Teknik", mimari: "Mimari", icerik: "İçerik", otorite: "Otorite", geo: "GEO",
};
const KATEGORI_STIL: Record<Is["kategori"], string> = {
  teknik: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  mimari: "bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  icerik: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  otorite: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  geo: "bg-pink-50 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300",
};
const EFOR_AD: Record<Is["efor"], string> = { dusuk: "düşük efor", orta: "orta efor", yuksek: "yüksek efor" };

export default function YolHaritasi() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const qc = useQueryClient();
  const [hedef, setHedef] = useState("");
  const [busy, setBusy] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const { data, isLoading } = useQuery<Yanit>({
    queryKey: ["yol-haritasi", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/yol-haritasi`);
      if (!r.ok) throw new Error("Yol haritası alınamadı");
      return r.json();
    },
  });

  const uret = async (): Promise<void> => {
    if (!activeDomainId) return;
    setBusy(true); setHata(null);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/yol-haritasi`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ hedef: hedef.trim() || undefined }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Plan üretilemedi");
      void qc.invalidateQueries({ queryKey: ["yol-haritasi", activeDomainId] });
    } catch (e) { setHata((e as Error).message); } finally { setBusy(false); }
  };

  const plan = data?.plan ?? null;
  const sinyaller = plan?.sinyaller ?? data?.sinyaller ?? [];

  return (
    <div className="px-4 sm:px-6 py-6 w-full max-w-(--breakpoint-2xl) mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Map className="w-5 h-5 text-brand-500" /> Yol Haritası
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          12 aylık stratejik plan: hangi işin hangi çeyrekte yapılacağı ve neden o sırada.
        </p>
      </div>

      <Card className="border-border bg-accent/40"><CardContent className="p-3 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <p className="text-[12px] text-muted-foreground">
          Bu ekran <b>üç katmanın en üstü</b>: Aksiyon Planı "şu an ne yapmalıyım", Strateji Seansı "bu ay ne
          yapacağız", burası "<b>bu yıl nereye gidiyoruz</b>" sorusunu yanıtlar. Sıralama mantığı şudur: önce delik
          kapatılır, sonra su dökülür. Sayfa dizine girmiyorken içerik yazmak boşa yatırımdır.
        </p>
      </CardContent></Card>

      {/* Plana giren ölçümler ÜRETMEDEN ÖNCE görünür: kullanıcı neyin temelinde
          plan çıkacağını bilsin, "bu nereden çıktı" diye sormasın. */}
      {sinyaller.length > 0 && (
        <Card><CardContent className="p-4">
          <div className="text-[11px] font-medium mb-2 text-muted-foreground">Plana giren ölçümler</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[12px]">
            {sinyaller.map((s) => (
              <span key={s.etiket}>
                <span className="text-muted-foreground">{s.etiket}:</span> <b className="tabular-nums">{s.deger}</b>
              </span>
            ))}
          </div>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-4 space-y-2">
        <label className="text-[12px] font-medium">Hedefin ne? (isteğe bağlı)</label>
        <div className="flex gap-2 flex-wrap">
          <input
            value={hedef} onChange={(e) => setHedef(e.target.value)}
            placeholder="Örnek: yurt dışından gelen hasta sayısını artırmak"
            className="flex-1 min-w-[240px] text-[13px] rounded-lg border px-3 py-2 bg-transparent"
            style={{ borderColor: "#e7e7e4" }}
          />
          <Button onClick={() => void uret()} disabled={busy || !activeDomainId} className="gap-1.5 shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {plan ? "Yeniden kur" : "Yol haritasını kur"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Hedef yazmazsan ölçülen duruma göre en mantıklı hedefi plan kendisi kurar ve gerekçesini söyler.
          Üretim yaklaşık bir dakika sürer ve <b>yaklaşık 10 sent</b> maliyeti vardır; bu yüzden sayfa açılınca
          kendiliğinden koşmaz.
        </p>
        {busy && <p className="text-[12px] text-muted-foreground">Plan kuruluyor, bu bir dakika kadar sürebilir…</p>}
        {hata && <p className="text-[12px] text-error-600">{hata}</p>}
      </CardContent></Card>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…
        </div>
      )}

      {!isLoading && !plan && !busy && (
        <Card><CardContent className="p-4 text-sm text-muted-foreground">
          Henüz bir yol haritası kurulmamış. Yukarıdaki butonla oluşturabilirsin.
        </CardContent></Card>
      )}

      {plan && (
        <>
          <Card><CardContent className="p-4 space-y-3">
            {plan.hedef && (
              <p className="text-[12px]"><span className="text-muted-foreground">Hedef:</span> <b>{plan.hedef}</b></p>
            )}
            <div>
              <div className="text-[11px] font-medium text-muted-foreground mb-1">Bugünkü durum</div>
              <p className="text-sm leading-relaxed">{plan.durumOzeti}</p>
            </div>
            <div>
              <div className="text-[11px] font-medium text-muted-foreground mb-1">Yılın hattı</div>
              <p className="text-sm leading-relaxed">{plan.anaHat}</p>
            </div>
          </CardContent></Card>

          <div className="space-y-3">
            {plan.ceyrekler.map((q) => (
              <Card key={q.no}><CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3 flex-wrap">
                  <span className="shrink-0 rounded-lg bg-brand-500/10 px-2.5 py-1 text-sm font-bold text-brand-600 dark:text-brand-300">
                    Ç{q.no}
                  </span>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-[11px] text-muted-foreground">{q.ay}</div>
                    <div className="text-sm font-semibold leading-snug">{q.tema}</div>
                  </div>
                </div>

                <p className="text-[12px] text-muted-foreground leading-relaxed">
                  <b className="text-foreground">Neden şimdi:</b> {q.nedenSimdi}
                </p>

                <ul className="space-y-2">
                  {q.isler.map((i, ix) => (
                    <li key={ix} className="border-l-2 border-border pl-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${KATEGORI_STIL[i.kategori]}`}>
                          {KATEGORI_AD[i.kategori]}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{EFOR_AD[i.efor]}</span>
                        <span className="text-[13px] font-medium">{i.baslik}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{i.neden}</p>
                    </li>
                  ))}
                </ul>

                <div className="rounded-lg bg-accent/50 px-3 py-2">
                  <p className="text-[12px]"><b>Çeyrek sonunda:</b> {q.olcut}</p>
                  {q.riskler && (
                    <p className="text-[11px] text-muted-foreground mt-1 flex items-start gap-1.5">
                      <TriangleAlert className="w-3 h-3 mt-0.5 shrink-0" /> {q.riskler}
                    </p>
                  )}
                </div>
              </CardContent></Card>
            ))}
          </div>

          {/* DÜRÜSTLÜK NOTU — kalıcı. Takvim garantisi satmıyoruz. */}
          <Card className="border-border bg-accent/40"><CardContent className="p-3 flex items-start gap-2">
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
            <p className="text-[12px] text-muted-foreground">{plan.uyari}</p>
          </CardContent></Card>

          <p className="text-[11px] text-muted-foreground px-1">
            {new Date(plan.uretildi).toLocaleString("tr-TR")} tarihinde kuruldu · üretim maliyeti ${plan.maliyet}
          </p>
        </>
      )}
    </div>
  );
}
